import React, { MutableRefObject, ReactNode, useEffect, useRef, useState } from "react";
import serviceWorldCaseStyle from "./serviceWorldCaseStyle";
import { Helmet } from "react-helmet-async";
import StatusBox from "./StatusBox/StatusBox";
import ProcessBox from "./ProcessBox/ProcessBox";
import { getCaseSendMessageLink, getProductData, getProductNameBy } from "../../services/productService";
import { Link, useLocation } from "react-router";
import LegalbirdLoader from "../ContentLoader/LegalbirdLoader";
import DocumentBox from "../PetitionSummary/DocumentBox";
import ServiceWorldCaseDetails from "./ServiceWorldCase/ServiceWorldCaseDetails";
import ServiceWorldCaseProductBoxes from "./ServiceWorldCase/ServiceWorldCaseProductBoxes";
import { Box, Button } from "@mui/material";
import Grid from "@mui/material/Grid";
import PageNavigation from "../Navigation/SummaryNavigation/PageNavigation";
import ServiceWorldCaseProductGuidebookBoxes from "./ServiceWorldCase/ServiceWorldCaseProductGuidebookBoxes";
import CalendlyAppointment from "../Calendly/CalendlyAppointment";
import { getAppointmentDisplayType } from "../../services/serviceWorldService";
import CustomerFeedbackBox from "./Common/CustomerFeedbackBox";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, fetchResource, queryKeys } from "../../services/reactQuery/reactQueryService";
import ServiceWorldSendMessageBox from "./ServiceWorldSendMessageBox";
import useProductParams from "../../hooks/useProductParams";
import { useCustomer } from "../../provider/Customer/CustomerProvider";
import InquiriesToCustomerCaseBox from "./InquiryToCustomer/InquiriesToCustomerCaseBox";
import { InquiryToCustomerStatus } from "../../types/Entities/InquiryToCustomer";
import { WarningAmberOutlined } from "@mui/icons-material";
import NewMessageCaseBox from "./Common/NewMessageCaseBox";
import ServiceWorldCaseDelete from "./ServiceWorldCaseDelete";
import { StatusBoxDeactivatedProduct } from "./StatusBox/StatusBoxDeactivatedProduct";
import { getInquiriesBoxHeadingContent } from "../../services/inquiryToCustomerService";
import CallBackPrototype from "./CallBackBox/CallBackPrototype";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import { gaSet } from "../../services/googleAnalyticsService";

const ServiceWorldCase = () => {
  const { productName, productId } = useProductParams();
  const productClassName = getProductNameBy("urlPartShort", productName);
  const productApiUri = getProductData(productClassName, "apiUri");
  const { data: product, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId)
  });
  const { customer } = useCustomer();
  const location = useLocation();
  const [caseIsDeletable, setCaseIsDeletable] = useState(false);

  const inquiryFilters = {
    productId: productId,
    "status[0]": InquiryToCustomerStatus.STATUS_OPEN,
    "status[1]": InquiryToCustomerStatus.STATUS_OVERDUE,
  };
  const inquiryApiUri = "/inquiry_to_customers";
  const { data: inquiries } = useQuery({
    queryKey: queryKeys.collection(inquiryApiUri, inquiryFilters),
    queryFn: () => fetchCollection(inquiryApiUri, inquiryFilters)
  });

  let refs: Record<string, MutableRefObject<HTMLElement | undefined>> = {
    "#documents": useRef(null!),
    "#details": useRef(null!),
    "#appointments": useRef(null!),
    "#inquiry": useRef(null!),
    "#deleteCase": useRef(null!),
  };

  useEffect(() => {
    if (location.hash && refs[location.hash] && refs[location.hash].current) {
      refs[location.hash].current?.scrollIntoView();
    }
  }, [product?.id]);

  const title = "Servicewelt Mein Fall | Legalbird";
  gaSet({ title: title });

  if (isLoading || !customer || !product) {
    return (
      <Grid
        container
        sx={[{
          justifyContent: "center"
        }, serviceWorldCaseStyle.loaderContainer]}>
        <Grid>
          <LegalbirdLoader />
        </Grid>
      </Grid>
    );
  }

  let navigationLinks: Array<{ target: string; label: string; icon?: ReactNode }> = [
    {
      target: "#status",
      label: "Jetzt vervollständigen",
    },
  ];

  if (product.isCustomerDataCompleted) {
    navigationLinks = [
      {
        target: "#status",
        label: "Status prüfen",
      },
      {
        target: "#documents",
        label: "Dokumente einsehen",
      },
      {
        target: getCaseSendMessageLink(product),
        label: "Nachricht senden / Dokumente hochladen",
      },
    ];
  }

  if (getAppointmentDisplayType(product) === "appointments") {
    navigationLinks.unshift({
      target: "#appointments",
      label: "Mein Beratungstermin",
    });
  }

  if (inquiries && inquiries.length > 0) {
    navigationLinks.unshift({
      target: "#inquiry",
      label: getInquiriesBoxHeadingContent(inquiries).headline,
      icon: <WarningAmberOutlined />,
    });
  }

  if (caseIsDeletable) {
    navigationLinks.push({
      target: "#deleteCase",
      label: "Fall löschen",
    });
  }

  let tertiaryHeadline;
  if (product.responsibleLawyer) {
    tertiaryHeadline = (product.responsibleLawyer.gender === "male" ? "Ihr Anwalt: " : "Ihre Anwältin: ") + product.responsibleLawyer.fullName;
  }

  return (
    <Box sx={serviceWorldCaseStyle.contentContainer}>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <PageNavigation
        navigationLinks={navigationLinks}
        headerIcon={getProductData(product.name, "productIcon")}
        headline={getProductData(product.name, "label")}
        subHeadline={product.reference}
        tertiaryHeadline={tertiaryHeadline}
      />
      <Box sx={serviceWorldCaseStyle.scrollToAnchor} id={"inquiry"} ref={refs["#inquiry"]} />
      {inquiries && inquiries.length > 0 && <InquiriesToCustomerCaseBox product={product} inquiries={inquiries} />}
      <Box sx={serviceWorldCaseStyle.scrollToAnchor} id={"appointments"} ref={refs["#appointments"]} />
      <CalendlyAppointment product={product} customer={customer} type={"advisoryAppointment"} />
      <CalendlyAppointment product={product} customer={customer} type={"lawyerCounselingCall"} />
      <Box sx={serviceWorldCaseStyle.scrollToAnchor} id={"status"} ref={refs["#status"]} />
      {product.isDeactivatedProduct ? <StatusBoxDeactivatedProduct /> : <StatusBox product={product} />}
      <CallBackPrototype product={product} />
      <CustomerFeedbackBox product={product} />
      <NewMessageCaseBox product={product} />
      <ProcessBox product={product} />
      <ServiceWorldCaseProductBoxes product={product} />
      <Box sx={serviceWorldCaseStyle.scrollToAnchor} id={"documents"} ref={refs["#documents"]} />
      <DocumentBox product={product} />
      <Box sx={serviceWorldCaseStyle.scrollToAnchor} id={"details"} ref={refs["#details"]} />
      <ServiceWorldCaseDetails product={product} />
      {product.isCustomerDataCompleted && <ServiceWorldSendMessageBox product={product} />}
      <ServiceWorldCaseProductGuidebookBoxes product={product} />
      <Box sx={serviceWorldCaseStyle.scrollToAnchor} id={"deleteCase"} ref={refs["#deleteCase"]} />
      <ServiceWorldCaseDelete product={product} setCaseIsDeletable={setCaseIsDeletable} caseIsDeletable={caseIsDeletable} />
      <Button fullWidth color="buttonGrey" component={Link} variant={"contained"} to={"/servicewelt"}>
        Zurück zur Servicewelt
      </Button>
    </Box>
  );
};

export default ServiceWorldCase;
